import { Box, Stack, Typography } from '@mui/material';
import type { TriggerType } from '@/types/dmAutomation';
import { ACCENT, ACCENT_TINT, TRIGGER_ICON, TRIGGER_LABEL } from '../_shared';
import type { WizardData } from './wizardTypes';

interface Props {
    data: WizardData;
    onChange: (patch: Partial<WizardData>) => void;
    compact?: boolean;
}

const TRIGGER_DESC: Record<TriggerType, string> = {
    COMMENT_KEYWORD: '내 게시물 댓글에 약속 단어가 달리면 그 사용자에게 자동으로 DM을 보내요',
    STORY_REPLY: '내 스토리에 답장을 남긴 사용자에게 자동으로 DM을 보내요',
    DM_KEYWORD: '내 DM 함에 약속 단어가 포함된 메시지가 오면 자동으로 답장을 보내요',
};

const TRIGGERS: TriggerType[] = ['COMMENT_KEYWORD', 'STORY_REPLY', 'DM_KEYWORD'];

const Step1Trigger = ({ data, onChange, compact }: Props) => {
    return (
        <Box>
            {!compact && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                        어떤 자동화를 추가하시겠어요?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        원하는 자동화를 골라주세요. 세부 내용은 다음 단계에서 설정할 수 있어요.
                    </Typography>
                </>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                {TRIGGERS.map((t) => {
                    const Icon = TRIGGER_ICON[t];
                    const selected = data.triggerType === t;
                    return (
                        <Box
                            key={t}
                            role="button"
                            tabIndex={0}
                            onClick={() => onChange({ triggerType: t })}
                            onKeyDown={(ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') onChange({ triggerType: t });
                            }}
                            sx={{
                                flex: 1,
                                bgcolor: selected ? ACCENT_TINT : 'background.paper',
                                border: '1.5px solid',
                                borderColor: selected ? ACCENT : 'divider',
                                borderRadius: 3,
                                p: 2.5,
                                textAlign: 'center',
                                cursor: 'pointer',
                                outline: 'none',
                                transition: 'border-color 120ms ease, background-color 120ms ease, transform 120ms ease',
                                willChange: 'transform',
                                '&:hover': { borderColor: ACCENT, transform: 'translateY(-1px)' },
                                '&:active': { transform: 'translateY(0)' },
                                '&:focus-visible': { borderColor: ACCENT, boxShadow: `0 0 0 3px ${ACCENT_TINT}` },
                            }}
                        >
                            <Icon sx={{ fontSize: 32, color: selected ? ACCENT : 'text.secondary', mb: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {TRIGGER_LABEL[t]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                {TRIGGER_DESC[t]}
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default Step1Trigger;
